import { snapRoundDecimal } from '@calculations/round/decimal';
import {
  QtyPerType,
  SnapUnsupportedCalculation,
  UnitsOfMeasure,
} from '@snap-types/models/enums';
import { SnapRequirement as BaseSnapRequirement } from '@snap-types/models/snap-requirement';
import { dec, type Decimal, ZERO } from '@snap-types/types/decimal';

// Some requirements have units of measure that aren't 1:1 with qtyPer, e.g.
// LBC - pounds per HUNDRED quantity (so divide the qtyPer by 100)
// BX50 - boxes of Fifty per (so, multiply the qty per by 50)
// see UNITS_CONVERSION table in VISUAL
const SUPPORTED_USAGE_UNITS_OF_MEASURE: readonly string[] = [
  UnitsOfMeasure.BAR,
  UnitsOfMeasure.BAR10,
  UnitsOfMeasure.BAR12,
  UnitsOfMeasure.BAR16,
  UnitsOfMeasure.BAR6,
  UnitsOfMeasure.CS,
  UnitsOfMeasure.CTN,
  UnitsOfMeasure.DAB,
  UnitsOfMeasure.EA,
  UnitsOfMeasure.FT,
  UnitsOfMeasure.GAL,
  UnitsOfMeasure.HR,
  UnitsOfMeasure.IN,
  UnitsOfMeasure.K,
  UnitsOfMeasure.KG,
  UnitsOfMeasure.LB,
  UnitsOfMeasure.LBS,
  UnitsOfMeasure.LOT,
  UnitsOfMeasure.LY15,
  UnitsOfMeasure.LY40,
  UnitsOfMeasure.LY42,
  UnitsOfMeasure.LY43,
  UnitsOfMeasure.LY50,
  UnitsOfMeasure.MM,
  UnitsOfMeasure.OZ,
  UnitsOfMeasure.PKG,
  UnitsOfMeasure.SET,
  UnitsOfMeasure.SQFT,
  UnitsOfMeasure.SQIN,
  UnitsOfMeasure.TOZ,
  UnitsOfMeasure.TUBE8OZ,
];

export type SnapRequirementCostParams = Pick<
  BaseSnapRequirement,
  | 'qtyPer'
  | 'qtyPerType'
  | 'fixedQty'
  | 'usageUnitOfMeasure'
  | 'unitMaterialCost'
  | 'unitLaborCost'
  | 'unitBurdenCost'
  | 'unitServiceCost'
  | 'fixedCost'
  | 'scrapPercent'
  | 'pieceNo'
  | 'partUuid'
>;

export type SnapRequirementCostResults = {
  burdenCost: Decimal;
  laborCost: Decimal;
  materialCost: Decimal;
  quantity: Decimal;
  serviceCost: Decimal;
  requiresUnsupportedCalculations: SnapUnsupportedCalculation[];
};

export function calculateRequirementCosts(
  {
    qtyPer,
    qtyPerType,
    fixedQty,
    usageUnitOfMeasure,
    unitMaterialCost,
    unitLaborCost,
    unitBurdenCost,
    unitServiceCost,
    fixedCost,
    scrapPercent,
  }: SnapRequirementCostParams,
  operationStartQuantity: Decimal,
  operationEndQuantity: Decimal,
): SnapRequirementCostResults {
  /** The input quantity of material for the operation associated with this requirement.
   * This quantity is calculated from the output quantity of
   * the operation and the operation's scrap values.
   * In Visual, this corresponds to the CALC_START_QTY field in the OPERATION table.*/

  // material is calculated on the operation's "start" or "end" quantity
  //  depending on the value of `qtyPerType`
  const operationQuantity =
    qtyPerType === QtyPerType.START
      ? operationStartQuantity
      : operationEndQuantity;

  // add back the % scrap for total material
  const scrapMultiplier = dec(1).plus(scrapPercent.dividedBy(100));

  const quantity = fixedQty.plus(
    qtyPer.times(operationQuantity).times(scrapMultiplier),
  );

  const materialCost = quantity.times(unitMaterialCost).plus(fixedCost ?? ZERO);

  const laborCost = quantity.times(unitLaborCost);
  const burdenCost = quantity.times(unitBurdenCost);
  const serviceCost = quantity.times(unitServiceCost);

  const usesUnsupportedUsageUoM =
    !!usageUnitOfMeasure &&
    !SUPPORTED_USAGE_UNITS_OF_MEASURE.includes(usageUnitOfMeasure);

  const requiresUnsupportedCalculations = usesUnsupportedUsageUoM
    ? [SnapUnsupportedCalculation.REQUIREMENT_USAGE_UOM_HAS_MULTIPLIER]
    : [];

  return {
    quantity: snapRoundDecimal(quantity),
    materialCost: snapRoundDecimal(materialCost),
    laborCost: snapRoundDecimal(laborCost),
    burdenCost: snapRoundDecimal(burdenCost),
    serviceCost: snapRoundDecimal(serviceCost),
    requiresUnsupportedCalculations,
  };
}
